import { useCallback, useEffect, useMemo, useState } from 'react';

import { DetailsValues } from '../../../components/Checkout/Details';
import {
  FramingChoice,
  FramingProduct,
  FramingSelections,
} from '../../../components/Checkout/Framing/types';
import {
  parseVariantTitle,
  resolveChoiceForProduct,
} from '../../../components/Checkout/Framing/utils/framePricing';
import { applyChoiceToSelections } from '../../../components/Checkout/Framing/utils/frameSelections';
import { OrderReviewItem } from '../../../components/Checkout/OrderReview';
import { useDrawStage } from '../../../hooks/useDrawStage';
import { useAuth } from '../../../providers/AuthProvider';
import { useDraws } from '../../../providers/DrawProvider';
import { useProducts } from '../../../providers/ProductProvider';
import { GeneratedProductReleaseType } from '../../../types/generated';
import { CountryCode, CurrencyCode } from '../../../types/Locations';
import { CHECKOUT_UNFRAMED, MIN_QUANTITY } from '../constants';
import {
  CheckoutEntryController,
  CheckoutEntrySelection,
  CheckoutFlowType,
  CheckoutIntentKind,
} from '../types';
import { buildAmlLines } from '../utils/aml';
import { trackCheckoutEntrySuccess } from '../utils/analytics';
import { resolveCheckoutFlowType } from '../utils/flowType';
import { buildFramingProducts } from '../utils/framingProducts';
import { resolveFrameByVariantId } from '../utils/framingQuery';
import { resolveCheckoutIntentKind } from '../utils/intentKind';
import { CheckoutOrderTotals } from '../utils/orderTotals';
import { PrivateRoomQuery } from '../utils/privateRoomQuery';
import { resolveReleaseType } from '../utils/releaseType';
import { DEFAULT_CHECKOUT_STEP } from '../utils/steps';
import { CheckoutWork } from '../utils/workView';
import { useCheckoutAmlCheck } from './useCheckoutAmlCheck';
import { useCheckoutAttributes } from './useCheckoutAttributes';
import { useCheckoutDisplayPrice } from './useCheckoutDisplayPrice';
import { useCheckoutHandoff } from './useCheckoutHandoff';
import {
  CheckoutSummaryProps,
  useCheckoutOrderValues,
} from './useCheckoutOrderValues';
import {
  CheckoutRedirectReturnController,
  useCheckoutRedirectReturn,
} from './useCheckoutRedirectReturn';
import { CheckoutStepController, useCheckoutStep } from './useCheckoutStep';
import { useDrawEntry } from './useDrawEntry';
import { useLinkedDrawEntry } from './useLinkedDrawEntry';
import { usePrivateRoomCheckout } from './usePrivateRoomCheckout';
import { useWorks } from './useWorks';

export type UseCheckoutArgs = {
  initialFrameVariantId?: string | null;
  initialWorkIds?: string[];
  privateRoom?: PrivateRoomQuery | null;
  productShopifyId: string;
  productSlug: string;
};

export type CheckoutController = {
  allowMultipleSelections: boolean;
  productShopifyId: string;
  artistName: string;
  checkoutLoading: boolean;
  clientSecret: string | null;
  confirmEntry: () => Promise<boolean>;
  currencyCode: CurrencyCode;
  detailsValues: DetailsValues | null;
  drawCloseAt: string | null;
  enteredWorkIds: string[];
  flowType: CheckoutFlowType | null;
  intentId: string | null;
  intentKind: CheckoutIntentKind | null;
  isLinkedDraw: boolean;
  isSingleDraw: boolean;
  prepareIntent: (details: {
    shippingAddressId: string | null;
    paymentMethodId: string | null;
    preOrder: boolean;
    countryCode: CountryCode | null;
    currencyCode: CurrencyCode;
  }) => Promise<boolean>;
  goToShopifyCheckout: () => Promise<void>;
  framingDialog: {
    close: () => void;
    confirm: (choice: FramingChoice, applyToAll?: boolean) => void;
    openFor: (productId: string) => void;
    showApplyToAll: boolean;
    targetProduct: FramingProduct | null;
  };
  framingProducts: FramingProduct[];
  framingSelections: FramingSelections;
  hasPendingFraming: boolean;
  identityVerificationRequired: boolean;
  isPreOrderFlow: boolean;
  maxQuantity: number;
  orderedItems: OrderReviewItem[];
  orderTotals: CheckoutOrderTotals;
  preOrdersEnabled: boolean;
  productSlug: string;
  redirectReturn: CheckoutRedirectReturnController;
  selectedWorkIds: string[];
  setDetailsValues: (values: DetailsValues) => void;
  setFramingSelections: (selections: FramingSelections) => void;
  setMaxQuantity: (maxQuantity: number) => void;
  step: CheckoutStepController;
  submitError: boolean;
  summaryProps: CheckoutSummaryProps;
  toggleWork: (productId: string, quantity?: number) => void;
  trackEntrySuccess: () => void;
  works: CheckoutWork[];
  workTitle: string;
};

export const useCheckout = ({
  initialFrameVariantId = null,
  initialWorkIds = [],
  privateRoom = null,
  productShopifyId,
  productSlug,
}: UseCheckoutArgs): CheckoutController => {
  const step = useCheckoutStep();
  const handoff = useCheckoutHandoff();
  const buildCustomAttributes = useCheckoutAttributes();
  const privateRoomCheckout = usePrivateRoomCheckout({
    privateRoom,
    productShopifyId,
  });
  const roomVariantIds = useMemo(() => {
    if (!privateRoom) return null;

    return Object.keys(privateRoomCheckout.room?.unlockCodes ?? {});
  }, [privateRoom, privateRoomCheckout.room]);
  const [shippingCountryCode, setShippingCountryCode] =
    useState<CountryCode | null>(null);
  const { isLoggedIn, user } = useAuth();
  const { getProduct } = useProducts();
  const {
    linkedDraws,
    mainDraw,
    selectedDraws,
    setWinningDrawsCount: setMaxQuantity,
    winningDrawsCount: maxQuantity,
  } = useDraws();
  const [chosenFraming, setChosenFraming] = useState<FramingSelections>({});
  const [detailsValues, setDetailsValues] = useState<DetailsValues | null>(
    null,
  );
  const [framingDialogProductId, setFramingDialogProductId] = useState<
    string | null
  >(null);

  const productEntry = getProduct(productShopifyId);
  const releaseType = privateRoom
    ? GeneratedProductReleaseType.Private
    : productEntry?.backendProduct?.releaseType;
  const stage = useDrawStage(mainDraw?.draw);
  const releaseFlowType = resolveCheckoutFlowType({ releaseType, stage });
  const flowType =
    releaseFlowType === 'draw' && detailsValues?.preOrderOptIn
      ? 'preOrder'
      : releaseFlowType;
  const isPreOrderFlow = flowType === 'preOrder';
  const allowMultipleSelections = flowType === 'timed' || flowType === 'fcfs';
  const intentKind = resolveCheckoutIntentKind(mainDraw?.draw);
  const isLinkedDraw = intentKind === 'linked';
  const isSingleDraw = intentKind === 'single';
  const { isDraw } = resolveReleaseType(releaseType);

  const worksController = useWorks({
    initialWorkIds,
    isDraw,
    productShopifyId,
    roomVariantIds,
    step,
  });
  const { enteredWorkIds, selectedWorkIds } = worksController;

  const allDraws = useMemo(
    () => (mainDraw ? [mainDraw, ...linkedDraws] : linkedDraws),
    [linkedDraws, mainDraw],
  );

  const productIdByDrawId = useMemo(
    () => new Map(allDraws.map((draw) => [draw.draw.id, draw.draw.productId])),
    [allDraws],
  );

  const amlCheck = useCheckoutAmlCheck({ countryCode: shippingCountryCode });

  const variantIdForProduct = useCallback(
    (productId: string | undefined): string | null | undefined => {
      return productId
        ? getProduct(productId)?.shopifyPurchaseDetails?.shopifyVariantId
        : undefined;
    },
    [getProduct],
  );

  const linkedEntry = useLinkedDrawEntry({
    drawCloseAt: mainDraw?.draw.closeAt ?? null,
    multisetID: mainDraw?.draw.multisetID || null,
    onConfirmed: (intent) => {
      return amlCheck.check(
        buildAmlLines(
          intent.lineItems.map((lineItem) => ({
            frameItems: lineItem.frameItems,
            variantId: variantIdForProduct(
              productIdByDrawId.get(lineItem.drawId),
            ),
          })),
        ),
        intent.maxQuantity,
      );
    },
    productIdByDrawId,
  });

  const singleEntry = useDrawEntry({
    drawId: mainDraw?.draw.id ?? null,
    onConfirmed: (intent) => {
      return amlCheck.check(
        buildAmlLines([
          {
            frameItems: intent.frameItems,
            variantId: variantIdForProduct(mainDraw?.draw.productId),
          },
        ]),
        1,
      );
    },
    productId: mainDraw?.draw.productId ?? null,
  });

  const entriesByKind: Record<CheckoutIntentKind, CheckoutEntryController> = {
    linked: linkedEntry,
    single: singleEntry,
  };
  const entry = intentKind ? entriesByKind[intentKind] : null;

  const {
    currencyCode,
    ready: displayPriceReady,
    toDisplayPrice,
  } = useCheckoutDisplayPrice({
    countryCode: shippingCountryCode,
    currencyCode: entry?.quoteCurrencyCode,
  });

  const confirmEntryById = useCallback(
    async (intentId: string): Promise<boolean> => {
      return entry ? entry.confirmEntry(intentId) : false;
    },
    [entry],
  );

  const redirectReturn = useCheckoutRedirectReturn(confirmEntryById);

  const works = useMemo(
    () =>
      worksController.works.map((work) => ({
        ...work,
        price: toDisplayPrice(work.price),
      })),
    [toDisplayPrice, worksController.works],
  );

  const worksById = useMemo(
    () => new Map(works.map((work) => [work.id, work])),
    [works],
  );

  const selectedWorks = useMemo(
    () =>
      selectedWorkIds
        .map((productId) => worksById.get(productId))
        .filter((work): work is CheckoutWork => Boolean(work)),
    [selectedWorkIds, worksById],
  );

  const framingProductsBase = useMemo(() => {
    return buildFramingProducts({
      getEntry: (shopifyId) => getProduct(shopifyId),
      selectedWorks,
    }).map((product) => ({
      ...product,
      frameOptions: product.frameOptions.map((option) => ({
        ...option,
        variants: option.variants.map((variant) => ({
          ...variant,
          consolidatedVariants: variant.consolidatedVariants.map(
            (consolidatedVariant) => ({
              ...consolidatedVariant,
              price: toDisplayPrice(consolidatedVariant.price),
            }),
          ),
        })),
      })),
    }));
  }, [getProduct, selectedWorks, toDisplayPrice]);

  const seededFrame = useMemo(() => {
    if (!initialFrameVariantId) return undefined;

    const product = framingProductsBase.find(
      (framingProduct) => framingProduct.id === productShopifyId,
    );
    if (!product) return undefined;
    if (initialFrameVariantId === CHECKOUT_UNFRAMED) return null;

    return resolveFrameByVariantId(product, initialFrameVariantId) ?? undefined;
  }, [framingProductsBase, initialFrameVariantId, productShopifyId]);

  const framingSelections = useMemo(() => {
    if (seededFrame === undefined) return chosenFraming;
    if (chosenFraming[productShopifyId] !== undefined) return chosenFraming;

    return { ...chosenFraming, [productShopifyId]: seededFrame };
  }, [chosenFraming, productShopifyId, seededFrame]);

  const entrySelection = useMemo<CheckoutEntrySelection>(
    () => ({ framingSelections, maxQuantity, selectedDraws }),
    [framingSelections, maxQuantity, selectedDraws],
  );

  const quote = useMemo(() => {
    return entry?.covers(entrySelection) ? entry.quote : null;
  }, [entry, entrySelection]);

  const { orderTotals, orderedItems, summaryProps } = useCheckoutOrderValues({
    allowMultipleSelections,
    currencyCode,
    framingSelections,
    maxQuantity,
    quote,
    selectedWorks,
    showPreference: isLinkedDraw,
    step: step.step,
  });

  const framingProducts = useMemo(
    () =>
      framingProductsBase.map((product) => ({
        ...product,
        initialFrame: framingSelections[product.id],
      })),
    [framingProductsBase, framingSelections],
  );

  const hasPendingFraming = useMemo(
    () =>
      framingProducts.some(
        (product) => framingSelections[product.id] === undefined,
      ),
    [framingProducts, framingSelections],
  );

  const framingDialogProduct = useMemo(
    () =>
      framingProducts.find(
        (product) => product.id === framingDialogProductId,
      ) ?? null,
    [framingDialogProductId, framingProducts],
  );

  const openFramingDialog = useCallback(
    (productId: string) => {
      setFramingDialogProductId(productId);
      step.goToStep(DEFAULT_CHECKOUT_STEP);
    },
    [step],
  );

  const closeFramingDialog = useCallback(
    () => setFramingDialogProductId(null),
    [],
  );

  const resetEntryUnlessCovered = useCallback(
    (next: Partial<CheckoutEntrySelection>) => {
      if (!entry || entry.covers({ ...entrySelection, ...next })) return;

      entry.reset();
      setDetailsValues(null);
    },
    [entry, entrySelection],
  );

  const applyFramingSelections = useCallback(
    (selections: FramingSelections) => {
      setChosenFraming(selections);
      resetEntryUnlessCovered({ framingSelections: selections });
    },
    [resetEntryUnlessCovered],
  );

  const changeMaxQuantity = useCallback(
    (nextMaxQuantity: number) => {
      setMaxQuantity(nextMaxQuantity);
      resetEntryUnlessCovered({ maxQuantity: nextMaxQuantity });
    },
    [resetEntryUnlessCovered, setMaxQuantity],
  );

  const confirmFramingDialog = useCallback(
    (choice: FramingChoice, applyToAll = false) => {
      if (framingDialogProduct) {
        applyFramingSelections(
          applyChoiceToSelections({
            applyToAll,
            choice,
            previous: framingSelections,
            products: framingProducts,
            targetIds: [framingDialogProduct.id],
          }),
        );
      }
      setFramingDialogProductId(null);
    },
    [
      applyFramingSelections,
      framingDialogProduct,
      framingProducts,
      framingSelections,
    ],
  );

  const isStepReachable = useMemo(() => {
    if (step.step === 'works') return true;

    if (redirectReturn.isReturn) {
      return !(step.step === 'success' && redirectReturn.status === 'failed');
    }

    const worksComplete = selectedWorkIds.length > 0 && !hasPendingFraming;
    if (step.step === 'details') return worksComplete;

    return worksComplete && detailsValues !== null && Boolean(isLoggedIn);
  }, [
    detailsValues,
    hasPendingFraming,
    isLoggedIn,
    redirectReturn.isReturn,
    redirectReturn.status,
    selectedWorkIds,
    step.step,
  ]);

  useEffect(() => {
    if (isStepReachable) return;

    step.goToStep(
      redirectReturn.status === 'failed' ? 'review' : DEFAULT_CHECKOUT_STEP,
    );
  }, [isStepReachable, redirectReturn.status, step]);

  useEffect(() => {
    if (!displayPriceReady || seededFrame === undefined) return;

    setChosenFraming((previous) => {
      if (previous[productShopifyId] !== undefined) return previous;

      return { ...previous, [productShopifyId]: seededFrame };
    });
  }, [displayPriceReady, productShopifyId, seededFrame]);

  useEffect(() => {
    if (!allowMultipleSelections) return;

    setChosenFraming((previous) => {
      let changed = false;
      const next = { ...previous };

      framingProducts.forEach((product) => {
        const selection = previous[product.id];
        if (!selection) return;

        const consolidatedQuantity =
          selection.variant.consolidatedVariants[0]?.quantity ?? MIN_QUANTITY;
        const quantity = worksById.get(product.id)?.quantity ?? MIN_QUANTITY;
        if (consolidatedQuantity === quantity) return;

        const { glazing, mounting } = parseVariantTitle(
          selection.variant.title,
        );
        const resolved = resolveChoiceForProduct(product, {
          glazing,
          mounting,
          styleTitle: selection.title,
        });
        if (resolved) {
          next[product.id] = resolved;
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [allowMultipleSelections, framingProducts, worksById]);

  const toggleWork = useCallback(
    (productId: string, quantity?: number) => {
      const change = worksController.toggleWork(productId, quantity);
      if (change) resetEntryUnlessCovered(change);
    },
    [resetEntryUnlessCovered, worksController],
  );

  const backendProduct = productEntry?.backendProduct ?? mainDraw?.product;

  const selectedDrawEntries = useMemo(
    () => allDraws.filter((item) => selectedDraws.includes(item.draw.id)),
    [allDraws, selectedDraws],
  );

  const trackEntrySuccess = useCallback(() => {
    trackCheckoutEntrySuccess({
      artistId: backendProduct?.artistIds?.[0],
      drawType: mainDraw?.draw.drawType,
      framingSelections,
      intentId: entry?.intentId ?? null,
      isPreOrder: isPreOrderFlow,
      preOrderOptIn:
        Boolean(mainDraw?.draw.preorderEnabled) &&
        Boolean(detailsValues?.preOrderOptIn),
      selectedEntries: selectedDrawEntries,
      user,
    });
  }, [
    backendProduct,
    detailsValues,
    entry,
    framingSelections,
    isPreOrderFlow,
    mainDraw,
    selectedDrawEntries,
    user,
  ]);

  const prepareIntent = useCallback(
    async (details: {
      shippingAddressId: string | null;
      paymentMethodId: string | null;
      preOrder: boolean;
      countryCode: CountryCode | null;
      currencyCode: CurrencyCode;
    }): Promise<boolean> => {
      redirectReturn.clear();
      setShippingCountryCode(details.countryCode);

      if (!entry) return true;

      return entry.submit(entrySelection, {
        currencyCode: details.currencyCode,
        customAttributes: await buildCustomAttributes({
          canBeFramed: framingProductsBase.length > 0,
        }),
        paymentMethodId: details.paymentMethodId,
        preOrder: details.preOrder,
        shippingAddressId: details.shippingAddressId,
      });
    },
    [
      buildCustomAttributes,
      entry,
      entrySelection,
      framingProductsBase.length,
      redirectReturn,
    ],
  );

  const confirmEntry = useCallback(async (): Promise<boolean> => {
    if (!entry?.intentId) return false;

    return entry.confirmEntry(entry.intentId);
  }, [entry]);

  const identityVerificationRequired = amlCheck.required;

  const goToShopifyCheckout = useCallback(async () => {
    if (privateRoomCheckout.loading || privateRoomCheckout.leaveIfExpired()) {
      return;
    }

    const items = selectedWorks.flatMap((work) => {
      const product = getProduct(work.id)?.shopifyPurchaseDetails;
      if (!product?.shopifyVariantId) return [];

      return [
        {
          frame: framingSelections[work.id] ?? null,
          product,
          quantity: work.quantity ?? 1,
        },
      ];
    });
    if (!items.length) return;

    await handoff.goToCheckout({
      currency: currencyCode,
      items,
      ...(privateRoomCheckout.room
        ? { privateRoom: privateRoomCheckout.room }
        : {}),
    });
  }, [
    currencyCode,
    framingSelections,
    getProduct,
    handoff,
    privateRoomCheckout,
    selectedWorks,
  ]);

  return useMemo(
    () => ({
      allowMultipleSelections,
      productShopifyId,
      artistName: backendProduct?.artists?.[0]?.name ?? '',
      checkoutLoading:
        handoff.loading ||
        privateRoomCheckout.loading ||
        Boolean(entry?.loading) ||
        Boolean(entry?.confirming),
      clientSecret: entry?.clientSecret ?? null,
      confirmEntry,
      currencyCode,
      detailsValues,
      drawCloseAt: mainDraw?.draw.closeAt ?? null,
      enteredWorkIds,
      flowType,
      goToShopifyCheckout,
      framingDialog: {
        close: closeFramingDialog,
        confirm: confirmFramingDialog,
        openFor: openFramingDialog,
        showApplyToAll: framingProducts.length > 1,
        targetProduct: framingDialogProduct,
      },
      framingProducts,
      framingSelections,
      hasPendingFraming,
      identityVerificationRequired,
      intentId: entry?.intentId ?? null,
      intentKind,
      isLinkedDraw,
      isPreOrderFlow,
      isSingleDraw,
      maxQuantity,
      orderedItems,
      orderTotals,
      preOrdersEnabled:
        releaseFlowType !== 'preOrder' &&
        (mainDraw?.draw.preorderEnabled ?? false),
      prepareIntent,
      productSlug,
      redirectReturn,
      selectedWorkIds,
      setDetailsValues,
      setFramingSelections: applyFramingSelections,
      setMaxQuantity: changeMaxQuantity,
      step,
      submitError: entry?.error ?? false,
      summaryProps,
      toggleWork,
      trackEntrySuccess,
      works,
      workTitle: backendProduct?.title ?? '',
    }),
    [
      allowMultipleSelections,
      applyFramingSelections,
      productShopifyId,
      backendProduct,
      changeMaxQuantity,
      closeFramingDialog,
      confirmEntry,
      confirmFramingDialog,
      currencyCode,
      detailsValues,
      enteredWorkIds,
      entry,
      flowType,
      framingDialogProduct,
      framingProducts,
      framingSelections,
      goToShopifyCheckout,
      handoff.loading,
      privateRoomCheckout.loading,
      hasPendingFraming,
      identityVerificationRequired,
      intentKind,
      isLinkedDraw,
      isPreOrderFlow,
      isSingleDraw,
      mainDraw,
      maxQuantity,
      openFramingDialog,
      orderTotals,
      orderedItems,
      prepareIntent,
      productSlug,
      redirectReturn,
      releaseFlowType,
      selectedWorkIds,
      step,
      summaryProps,
      toggleWork,
      trackEntrySuccess,
      works,
    ],
  );
};
